<template>
  <div class="article">
    <div
      v-if="!isPreview && shouldShowAd(topBanner) && !topBanner.aboveHeader && !renderTopBannerBeforeBodyText(topBanner)"
      class="top-ad-placeholder"
    >
      <dn-ad
        :ad="topBanner"
        class="full top-ad-below-header"
        data-zone="top-ad"
      />
    </div>

    <article
      v-if="article"
      v-show="loaded"
      class="dn-article"
      :datalayout="article.dataLayout"
      :theme="article.theme"
    >
      <dn-ma-frame
        v-if="!isPreview && shouldRenderBannerCard(topBannerCard)"
        :id="topBannerCard.id"
        class="dn-ma-frame-top"
      />
      <dn-article-top-wrapper
        data-zone="article-top"
        data-list
        :article="article"
        :breadcrumb="{
          text: breadcrumbText,
          path: breadcrumbPath
        }"
        :has-sharing-access="hasSharingAccess"
        :show-gift-article-option="showGiftArticleOption"
        @open-share-modal="openShareModal"
      />
      <div class="dn-published-date">
        <div
          class="dn-published-and-buttons"
          data-list="reverse"
        />
        <div
          class="dn-published-and-buttons"
          data-list="reverse"
        />
        <client-only>
          <dn-article-published-date />
        </client-only>
      </div>
      <client-only>
        <dn-audio-player :article-id="article.id" />
      </client-only>
      <div
        ref="dnContent"
        class="dn-content"
        data-zone="article-body"
      >
        <div
          v-if="!isPreview && shouldShowAd(topBanner) && renderTopBannerBeforeBodyText(topBanner)"
          class="top-ad-placeholder-mobile"
        >
          <dn-ad
            :ad="topBanner"
            class="full top-ad-below-header"
            data-zone="top-ad"
          />
        </div>
        <template v-if="rtConfPub.auth">
          <nuxt-lazy-hydrate>
            <dn-article-inline-content
              id="dn-content-ssr"
              :content="body || []"
              :clipped="clipped"
              :ssr="true"
              :hide-factbox-image-caption="isFeatureArticle"
            />
          </nuxt-lazy-hydrate>
          <client-only>
            <dn-article-inline-content
              id="dn-content"
              :class="{ shadow: clipped }"
              :content="body || []"
              :clipped="clipped"
              :ssr="false"
              :hide-factbox-image-caption="isFeatureArticle"
            >
              <template #bodyTop>
                <div
                  v-track-scroll="{ eventName: 'Article body top', slot: 'article_body_top', component: { zone: 'article-body', element: 'dn-content' }, trackOptions: { trackCount: 1, trackDirection: 'top' } }"
                />
              </template>

              <template #bodyBottom>
                <div
                  v-track-scroll="{ eventName: 'Article body bottom', slot: 'article_body_bottom', component: { zone: 'article-body', element: 'dn-content' }, trackOptions: { trackCount: 1, trackDirection: 'top' } }"
                />
                <dn-tip-box
                  v-if="!clipped && article.metadata.tipBox && article.metadata.tipBox !== 'none'"
                  :variant="article.metadata.tipBox"
                  :authors="article.byline?.authors ?? []"
                />
              </template>
            </dn-article-inline-content>
          </client-only>
        </template>
        <template v-else>
          <dn-article-inline-content
            id="dn-content"
            :class="{ shadow: clipped }"
            :content="body || []"
            :clipped="clipped"
            :ssr="false"
            :hide-factbox-image-caption="isFeatureArticle"
          >
            <template #bodyTop>
              <div
                v-track-scroll="{ eventName: 'Article body top', slot: 'article_body_top', component: { zone: 'article-body', element: 'dn-content' }, trackOptions: { trackCount: 1, trackDirection: 'top' } }"
              />
            </template>

            <template #bodyBottom>
              <div
                v-track-scroll="{ eventName: 'Article body bottom', slot: 'article_body_bottom', component: { zone: 'article-body', element: 'dn-content' }, trackOptions: { trackCount: 1, trackDirection: 'top' } }"
              />
              <dn-tip-box
                v-if="!clipped && article.metadata.tipBox && article.metadata.tipBox !== 'none'"
                :variant="article.metadata.tipBox"
                :authors="article.byline?.authors ?? []"
              />
            </template>
          </dn-article-inline-content>
        </template>
      </div>
    </article>
    <div
      v-show="!clipped"
      class="dn-below-article-interaction"
      data-zone="article-bottom"
    >
      <div
        class="action"
        data-list
      >
        <div class="meta-info">
          <dn-date-time
            class="published"
            :value="article.firstPublishedAt || article.publishedAt"
            :format="rtConfPub?.article?.timeFormat?.published || rtConfPub?.timeFormat || 'YYYY-MM-DD'"
            content="Published {date}"
          />
          <dn-date-time
            class="updated"
            :value="article.updatedAt"
            :format="rtConfPub?.article?.timeFormat?.updated || rtConfPub?.timeFormat || 'YYYY-MM-DD'"
            content="Updated {date}"
          />
        </div>
        <template
          v-for="(tag, index) in (article?.tags || []).slice(0, 5)"
          :key="index"
        >
          <atlas-tag
            v-track-event="{ eventName: 'Tag', component: { zone: 'article-bottom', element: 'dn-tag' } }"
            :title="tag.label"
            :to="isTopicPublication ? `/topic/${tag.name}` : `/tag/${tag.name}`"
            external
          />
        </template>
      </div>
    </div>
    <div
      v-show="!clipped"
      class="interaction-group"
      data-zone="article-bottom"
      data-list
    >
      <client-only>
        <dn-copy-link
          v-track-event="{ eventName: 'Copy link', component: { zone: 'article-bottom', element: 'dn-copy-link' } }"
        />
        <dn-save-button
          v-if="showBookmarkButton"
          v-track-event="{ eventName: 'Save article', component: { zone: 'article-bottom', element: 'dn-save' }, attributes: { element_position: '2/3' } }"
          :article-id="article.id"
        />
        <dn-give-away-button v-if="showGiftArticleOption && rtConfPub.auth">
          <atlas-button
            v-if="hasSharingAccess"
            v-track-event="{ eventName: 'Give away', component: { zone: 'article-bottom', element: 'dn-give-away' } }"
            class="secondary"
            :title="t('Gift')"
            icon="common/gift"
            @click="openShareModal"
          />
        </dn-give-away-button>
        <dn-pdf-button
          v-track-event="{ eventName: 'pdf', component: { zone: 'article-bottom', element: 'dn-pdf' } }"
        />
        <dn-be-notified
          v-if="rtConfPub.alerts && (article?.tags || []).length > 0"
          :is-tag-view="true"
          :text="t('Be notified')"
        >
          <dn-alert-subscription
            id="alert-topics"
            :title="t('Choose topics')"
            :description="t('You create an alert for all articles written about:')"
            type="topics"
            :items="article.tags.slice(0, 5)"
          />
        </dn-be-notified>
      </client-only>
    </div>
    <lazy-client-only v-if="rtConfPub.auth">
      <dn-paywall
        v-detect-position="{ type: 'dn-paywall', key: 'dn-paywall',
                             action: [!!articleAdConfig?.containSticky980 ? 'sticky-stop' : 'sticky-hide'] }"
      />
    </lazy-client-only>
    <div class="dn-feed">
      <dn-ma-frame
        v-if="shouldRenderBannerCard(bannerBox2Card)"
        :id="bannerBox2Card.id"
      />
      <client-only v-if="shouldShowAd(premium)">
        <nuxt-lazy-hydrate :when-idle="1000">
          <dn-ad
            v-detect-position="getDetectPositionPayload(premium,[!!articleAdConfig?.containSticky980 ? 'sticky-stop' : 'sticky-hide'])"
            :ad="premium"
            class="follow-grid wide-ad"
          />
        </nuxt-lazy-hydrate>
      </client-only>
      <client-only v-if="shouldShowAd(streamBillboard)">
        <nuxt-lazy-hydrate :when-idle="1000">
          <dn-ad
            v-detect-position="getDetectPositionPayload(streamBillboard,[!!articleAdConfig?.containSticky980 ? 'sticky-stop' : 'sticky-hide'])"
            :ad="streamBillboard"
            class="follow-grid wide-ad"
          />
        </nuxt-lazy-hydrate>
      </client-only>
      <nuxt-lazy-hydrate :when-visible="{ rootMargin: '600px' }">
        <lazy-client-only v-if="rtConfPub.appEnv !== 'prod'">
          <dn-grid-streams
            v-detect-position="{ type: 'element', key: 'related_stream', action: ['sticky-stop'] }"
            data-zone="article-related"
            provider="llm"
            :options="{ size: 4, id: article.id }"
            :title="t('Read more')"
          >
            <template #default="data">
              <dn-teaser-card-old
                v-if="data?.item"
                v-track-event="() => ({ eventName: 'Related article', component: { zone: 'article-related', element: 'dn-teaser-card-old' }, attributes: { ...data.item }, content: { canonical_slug: data.item.canonicalSlug, paid: data.item.accessLevel === 'paywall', category: data.item.categories?.find(category => category.default)?.title || data.item.categories?.[0]?.title } })"
                :article="data.item"
                :class="[{ row: [1, 6].includes(data.position) }]"
              />
            </template>
          </dn-grid-streams>
        </lazy-client-only>
      </nuxt-lazy-hydrate>
      <div v-viewed="{func: readMoreViewed, params:[{beforeMargin: '600'},'viewed' ]}">
        <lazy-client-only>
          <dn-grid-streams
            v-if="isReadMoreVisible"
            v-detect-position="{ type: 'element', key: 'article_stream', action: ['sticky-stop'] }"
            class="article-stream"
            data-zone="article-stream"
            provider="kilkaya"
            :options="{ size: kilkayaStreamSize, section: 'article', skipViewed: true }"
            :title="t('Read more')"
            wrapper="div"
          >
            <template #prefix>
              <div
                v-track-scroll="{ eventName: 'Kilkaya Streams top', slot: 'kilkaya_streams_top', component: { zone: 'article-stream', element: 'dn-grid-streams' }, trackOptions: { trackCount: 1, trackDirection: 'top' } }"
              />
            </template>
            <template #default="data">
              <dn-teaser-card-old
                v-if="data?.item && !isAd(data?.item?.type)"
                v-viewed.once="{ func: useItemVisited().action, params: [{ id: data.item.id, type: 'teaser' }, 'viewed'] }"
                v-track-event="() => ({ eventName: 'Kilkaya stream article', component: { zone: 'article-stream', element: 'dn-teaser-card-old' }, attributes: { ...data.item }, content: { canonical_slug: data.item.canonicalSlug, paid: data.item.accessLevel === 'paywall', category: data.item.categories?.find(category => category.default)?.title || data.item.categories?.[0]?.title } })"
                :article="data.item"
                :class="getStreamClass(data.position)"
                @click.once="useItemVisited().action({ id: data.item.id, type: 'teaser' }, 'clicked')"
              />
            </template>
            <template #ad="data">
              <dn-ad
                v-if="isAd(data?.item?.type) && shouldShowAd(data?.item)"
                :ad="data?.item"
                class="row full-width no-background"
              />
            </template>

            <template #suffix>
              <div
                v-track-scroll="{ eventName: 'Kilkaya Streams bottom', slot: 'kilkaya_streams_bottom', component: { zone: 'article-stream', element: 'dn-grid-streams' }, trackOptions: { trackCount: 1, trackDirection: 'top' } }"
              />
            </template>
          </dn-grid-streams>
        </lazy-client-only>
      </div>

      <client-only v-if="shouldShowAd(brandBoard3)">
        <nuxt-lazy-hydrate :when-idle="1000">
          <dn-ad
            v-detect-position="getDetectPositionPayload(brandBoard3,['sticky-hide'])"
            :ad="brandBoard3"
            class="full wide-ad"
          />
        </nuxt-lazy-hydrate>
      </client-only>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';
import { AtlasTag, AtlasButton } from '@nhst/ui';
import { useArticleStore } from '~/store/article';
import { useProfileLoginStore } from '~/store/profileLogin';
import { type PublicationContext } from '~/types';

const { t } = useI18n();
const nuxtApp = useNuxtApp();
const articleStore = useArticleStore();
const profileLogin = useProfileLoginStore();
const dayjs = useDayjs();
const isPreview = inject<Ref<boolean>>('isPreview', ref(false));

/**
 * check if the route match the article id regex
 */

definePageMeta({
  path: '/:pathMatch(.*)?/:lcsId([0-9]\\-[0-9]\\-[0-9]+|[0-9]-[0-9]-[0-9]+PT[0-9]+)',
  layout: 'default'
});

const { replaceUrl } = useUrlReplace();
const route = useRoute();
const isReadMoreVisible = ref(false);
const lcsId = (route.params?.lcsId as string) || '';
const loaded = ref(false);
const article = (await articleStore.getArticleContent(lcsId)) || {};

if (import.meta.server && article.updatedAt) {
  const event = useRequestEvent();
  if (event) {
    event.context.updatedAt = article.updatedAt;
  }
}

const rtConfPub = useRuntimeConfig().public as unknown as PublicationContext;

if (!article || !article.id) {
  throw createError({ statusCode: 404, statusMessage: 'Article Not Found' });
} else if (article && article.id !== lcsId) {
  throw createError({ statusCode: 500, statusMessage: `Serve wrong article: articleId: ${article.id} lcsId: ${lcsId}` });
} else if (article && rtConfPub.disAllowedArticleTypes.includes(article.type)) {
  throw createError({ statusCode: 404, statusMessage: `Article type is ${article.type} content, This has been disallowed from the configuration` });
}

nuxtApp.callHook('cpp:data:loaded', { type: 'article', title: useArticle().getMetaTitle(), data_layout: article?.dataLayout });

loaded.value = true;

const { article: articleAdConfig } = getAdConfig();

const topBanner = article?.metadata?.removeTopAds === true ? null : articleAdConfig?.static?.topBanner;
const premium = articleAdConfig?.static?.premium;
const streamBillboard = articleAdConfig?.static?.streamBillboard;
const brandBoard3 = articleAdConfig?.static?.brandBoard3;

const topicPublication = ['dn', 'd2', 'magasinet'];

const isTopicPublication = topicPublication.includes(rtConfPub.name);

const brazeConfig = getBrazeConfig();
const articleBannerConfig = brazeConfig.bannerConfig?.article;

const topBannerCard = articleBannerConfig?.static?.topBanner;
const bannerBox2Card = articleBannerConfig?.static?.bannerBox2;

const clipped = computed(() => article.body?.filter(i => !i.injected).length < article.bodyLength);
const body = computed(() => (clipped.value ? article.body?.filter(i => i.type !== 'embed') : article.body));
const defaultCategory = article.categories ? article.categories.find(category => category.default) || article.categories[0] : null;
const isFeatureArticle = article.subType === 'featureArticle';

const kilkayaStreamSize = rtConfPub?.kilkaya?.options?.size || 30;

const breadcrumbPath = ref<string>(
  replaceUrl(
    rtConfPub.breadcrumb.path,
     (rtConfPub.showCategoryAsTopic && defaultCategory ? { category: defaultCategory.id as string } : { topic: article.topic?.name as string }) as Record<string, string>
  )
);
const readMoreViewed = () => {
  isReadMoreVisible.value = true;
};
const breadcrumbText = ref<string>(rtConfPub.showCategoryAsTopic && defaultCategory ? defaultCategory.title : article.topic?.label);
const showBookmarkButton = !!rtConfPub.showBookmarkOption && profileLogin.loginStatus.isLoggedIn;
const showGiftArticleOption = rtConfPub.showGiftArticleOption && profileLogin.loginStatus.isLoggedIn && article.accessLevel === 'paywall';
const { openShareModal, hasSharingAccess } = useArticleGiveAway();

const dnContent = ref(null);

watch(
  () => articleStore.currentArticle.id,
  () => {
    useItemVisited().action({ id: articleStore.currentArticle.id, type: 'teaser' }, 'clicked');
  },
  { deep: true, immediate: true }
);

const trackBrazeArticleVisit = () => {
  const { $braze } = useNuxtApp();
  if (!$braze) {
    return;
  }

  const oneId = getOneId();
  if (!oneId) {
    return;
  }

  const articleVisitEvent = brazeConfig.customEvents;

  const brazeEventTopics: string[] = articleVisitEvent?.topics || [];
  const topicName = article?.topic?.name?.toLowerCase();
  const isTopicMatch = topicName && brazeEventTopics.includes(topicName);

  if (!isTopicMatch) {
    return;
  }

  const eventProperties: Record<string, string> = {
    topic: article.topic?.name || ''
  };

  if (useArticle().isOpinionArticle.value) {
    const brazeEventAuthors: string[] = articleVisitEvent?.authors || [];
    const authors = article.byline?.authors?.map((author: { name: string }) => author.name) || [];
    const isAuthorMatch = authors.some((authorName: string) => brazeEventAuthors.includes(authorName));

    if (isAuthorMatch) {
      eventProperties.author = authors.join(' | ') || '';
    }
  }
  if (articleVisitEvent?.id) {
    $braze.logCustomEvent(articleVisitEvent.id, eventProperties);
  }

  if (articleVisitEvent?.source) {
    eventProperties.source = articleVisitEvent.source;
  }
};

onMounted(() => {
  const ssrContent = document.getElementById('dn-content-ssr');
  if (ssrContent && ssrContent.innerHTML.length > 0) {
    ssrContent.innerHTML = '';
  }
  stickyTopPosition();
  trackBrazeArticleVisit();
});

const stickyTopPosition = () => {
  const pushDownSticky = articleAdConfig?.pushDownSticky;
  const articleStore = useArticleStore();
  const theme = articleStore.currentArticle?.metadata?.theme;
  const dataLayout = articleStore.currentArticle?.dataLayout;
  const dataLayoutParts = dataLayout?.split(' ') ?? [];

  const shouldPushDown =
    pushDownSticky?.themes?.includes(theme) ||
    pushDownSticky?.dataLayouts?.some((layout: string) => dataLayoutParts.includes(layout));

  const stickyPosition = shouldPushDown ? `${dnContent?.value?.offsetTop ?? 0}px` : '0px';
  document.documentElement.style.setProperty('--sticky-top-position', stickyPosition);
};

const loading = articleStore.loading;

provide('loading', loading);

const ogSite = rtConfPub?.seo?.siteName || useRequestURL()?.hostname.replace('www.', '');

const requestURL = useRequestURL();
const abTestValue = requestURL.searchParams.get('abtest');
const pageUrl = `${requestURL.protocol}//${rtConfPub.hostname}${article?.canonicalSlug}${abTestValue ? `?abtest=${abTestValue.toLowerCase()}` : ''}`;

const tagLink = (keyword: string) => {
  return rtConfPub?.tag?.path?.replace(':keyword', keyword);
};

function getMeta () {
  const metaImage = useArticle().getMetaImage(rtConfPub?.seo?.fallbackImage);
  const ogImage = useImage(metaImage.src, { imageCrops: metaImage.imageCrops, ratio: '2x1' }).getImageSrc('webp', 1200);
  const headMeta = [
    {
      name: 'description',
      content: useArticle().getMetaDescription
    },
    {
      property: 'og:title',
      content: useArticle().getSoMeTitle(rtConfPub?.article?.paidSoMeTitleSuffix)
    },
    {
      property: 'og:description',
      content: useArticle().getMetaDescription
    },
    {
      property: 'og:type',
      content: 'article'
    },
    {
      property: 'og:url',
      content: pageUrl
    },
    {
      property: 'og:image',
      content: ogImage
    },
    {
      property: 'og:image:width',
      content: 1000
    },
    {
      property: 'og:image:height',
      content: 600
    },
    {
      property: 'og:site_name',
      content: ogSite
    },
    {
      property: 'og:site',
      content: ogSite
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image'
    },
    {
      name: 'twitter:site',
      content: ogSite
    },
    {
      name: 'twitter:title',
      content: useArticle().getSoMeTitle(rtConfPub?.article?.paidSoMeTitleSuffix)
    },
    {
      name: 'twitter:description',
      content: useArticle().getMetaDescription
    },
    {
      name: 'twitter:creator',
      content: ogSite
    },
    {
      name: 'twitter:image:src',
      content: ogImage
    },
    {
      name: 'twitter:domain',
      content: rtConfPub.hostname
    }
  ];

  const robotsDefault = (rtConfPub?.seo?.robots || 'index,follow').split(',');
  const robots = ['max-image-preview:large'];
  robots.push(article?.subType === 'ft_article' || article?.metadata?.excludeFromIndex || robotsDefault.includes('noindex') ? 'noindex' : 'index');
  robots.push(robotsDefault.includes('nofollow') ? 'nofollow' : 'follow');

  if (article.accessLevel === 'paywall') {
    robots.push('noarchive');
  }
  headMeta.push({ name: 'robots', content: robots.join(',') });

  if (rtConfPub?.fb?.appId) {
    headMeta.push({ name: 'fb:app_id', content: rtConfPub.fb.appId });
  }

  if (rtConfPub?.fb?.pageId) {
    headMeta.push({ name: 'fb:pages', content: rtConfPub.fb.pageId });
  }
  for (const author of article?.byline?.authors || []) {
    headMeta.push({
      property: 'article:author',
      content: author.name
    });
  }

  if (breadcrumbText.value) {
    headMeta.push({ name: 'article:section', content: breadcrumbText.value });
  }

  if (article?.firstPublishedAt) {
    headMeta.push({ name: 'article:published_time', content: article?.firstPublishedAt });
  }

  if (article?.updatedAt) {
    headMeta.push({ name: 'article:modified_time', content: article?.updatedAt });
  }

  if (article?.topic?.label) {
    headMeta.push({ property: 'article:tag', content: article.topic.label });
  }

  for (const tag of article?.tags || []) {
    headMeta.push({ property: 'article:tag', content: tag.label });
  }

  return headMeta.concat(useArticle().getPaywallMeta());
}
const streamClassesArticle = ref({
  row: [1, 8, 15],
  reverse: [8],
  inverted: [1, 15, 21, 25],
  brand: [8, 12],
  fullwidthmobile: [2, 3, 4, 5, 6, 7, 9, 10, 11, 12, 13, 14, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36]
});
if (typeof rtConfPub?.streamClassesArticle !== 'undefined') {
  const keys = Object.keys(rtConfPub?.streamClassesArticle);
  for (let i = 0; i < keys.length; i++) {
    streamClassesArticle.value[keys[i]] = rtConfPub?.streamClassesArticle[keys[i]];
  }
}

function getStreamClass (streamIndex: number) {
  return Object.keys(streamClassesArticle.value).filter(key => streamClassesArticle.value[key].includes(streamIndex));
}

useHead({
  title: useArticle().getMetaTitle,
  meta: getMeta(),
  htmlAttrs: {
    datalayout: article?.dataLayout,
    theme: article?.metadata?.theme
  },
  bodyAttrs: {
    class: rtConfPub.name
  }
});

const metaImage = useArticle().getMetaImage(rtConfPub?.seo?.fallbackImage);

const jsonLdImage = [
  useImage(metaImage.src, { imageCrops: metaImage.imageCrops, ratio: '1x1' }).getImageSrc('webp', 600),
  useImage(metaImage.src, { imageCrops: metaImage.imageCrops, ratio: '4x3' }).getImageSrc('webp', 1200),
  useImage(metaImage.src, { imageCrops: metaImage.imageCrops, ratio: '16x9' }).getImageSrc('webp', 1600)
];

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'NewsArticle',
      name: article.title,
      headline: article.title,
      url: pageUrl,
      author: article.byline?.authors?.map((author) => {
        return {
          '@type': 'Person',
          name: author.name,
          email: author.email
          /*
           * author url will be added after creating author page
           * url:
           */
        };
      }),
      dateModified: article?.updatedAt ? article.updatedAt : undefined,
      datePublished: article?.firstPublishedAt ? article.firstPublishedAt : undefined,
      image: jsonLdImage,
      publisher: {
        '@type': 'Organization',
        name: rtConfPub?.title
      }
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: breadcrumbText.value,
          item: breadcrumbPath.value
        }
      ]
    }
  ]
}));
</script>

<style lang="scss">
.article {
  .dn-article {
    display: flex;
    flex-direction: column;

    --dn-image_background: transparent;

    .shadow {
      position: relative;

      &::before {
        content: ' ';
        position: absolute;
        height: 200px;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(180deg, hsl(48deg 33% 97% / 0%) 0, var(--color-page-bg));
      }
    }

    img {
      max-inline-size: 100%;
      block-size: auto;
    }
  }

  .adobe-target-placeholder {
    width: 100%;
  }

  .layout {
    max-width: var(--grid-width);
    margin: 0 auto;

    @media only screen and (width >= 979px) {
      padding: 0 var(--row-gap);
    }
  }

  .dn-published-date {
    margin-bottom: var(--row-gap-article);

    .dn-date-time {
      display: block;
      inline-size: min(var(--content-width), 100vw - var(--site-padding) * 2);
      margin-left: auto;
      margin-right: auto;
    }
  }

  .opinion + .dn-published-date {
    @media only screen and (width < 980px) {
      margin-top: var(--row-gap);
    }
  }

  .dn-published-and-buttons {
    display: flex;
    flex-direction: row-reverse;
    column-gap: 1rem;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    font-family: var(--typography-utility-s-font-family);
    font-size: var(--typography-utility-s-font-size);
    letter-spacing: var(--typography-utility-s-letter-spacing);
    line-height: var(--typography-utility-s-line-height);
    font-weight: var(--typography-utility-s-font-weight);
    text-align: left;

    @media only screen and (width >= 768px) {
      max-width: 100%;
      flex-direction: row-reverse;
      justify-content: flex-start;
      font-size: var(--typography-utility-m-font-size);
      letter-spacing: var(--typography-utility-m-letter-spacing);
      line-height: var(--typography-utility-m-line-height);
      font-family: var(--typography-utility-m-font-family);
      font-weight: var(--typography-utility-m-font-weight);
      text-align: right;
    }
  }

  .meta-info {
    width: 100%;
    font-family: var(--typography-utility-s-font-family);
    font-size: var(--typography-utility-s-font-size);
    letter-spacing: var(--typography-utility-s-letter-spacing);
    line-height: var(--typography-utility-s-line-height);
    font-weight: var(--typography-utility-s-font-weight);
    border-bottom: 1px solid var(--color-page-divider);
    padding-bottom: var(--ui-gap);

    @media only screen and (width >= 768px) {
      font-size: var(--typography-utility-m-font-size);
      letter-spacing: var(--typography-utility-m-letter-spacing);
      line-height: var(--typography-utility-m-line-height);
      font-family: var(--typography-utility-m-font-family);
      font-weight: var(--typography-utility-m-font-weight);
    }
  }

  .dn-below-article-interaction .action,
  .dn-below-article-interaction .meta-info {
    inline-size: min(var(--content-width), 100vw - calc(var(--site-padding) * 2));
    margin: 0 auto;
    display: flex;
    flex-flow: row wrap;
    gap: var(--ui-gap) 8px;
  }

  .dn-below-article-interaction .action {
    padding-top: var(--row-gap);
    margin-block-end: var(--row-gap);
  }

  .interaction-group {
    display: flex;
    flex-wrap: wrap;
    inline-size: min(var(--content-width), 100vw - calc(var(--site-padding) * 2));
    margin: 0 auto;
    margin-block-end: var(--row-gap);
    gap: 8px;
  }

  .topic-dateline {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: var(--row-gap);
    font-family: var(--typography-utility-test-font-family);
    color: var(--color-typography-secondary);
    font-size: 0.9rem;

    @media only screen and (width >= 980px) {
      justify-content: flex-end;
    }
  }

  .updated::before {
    content: var(--time-delimiter-symbol);
    padding-right: 24px;
  }

  .dn-feed {
    grid-area: feed;
    grid-column: 1 / 4;

    --site-padding: 0;

    @media only screen and (width >= 980px) {
      --site-padding: inherit;
    }

    @media only screen and (width <= 980px) {
      .dn-section-title {
        margin: 1rem 12px;
      }
    }

    > :not(.center, .wide, .full, .dn-live-wrapper-ad) {
      inline-size: min(var(--feed-width), 100vw - calc(var(--site-padding) * 2));
      margin: 0 auto;
    }

    > .dn-live-wrapped-ad {
      &.view-ads {
        margin-top: var(--ui-gap);

        + .dn-live-wrapped-ad {
          &.view-ads {
            margin-top: var(--row-gap);
          }
        }
      }
    }
  }

  .article-stream .dn-grid li {
    &:first-child,
    &:nth-child(5),
    &:nth-child(7) {
      grid-column: span 12;
    }

    &:nth-child(4),
    &:nth-child(6) {
      grid-column: span 12;

      @media only screen and (width >= 768px) {
        grid-column: span 4;
      }
    }

    &:nth-child(n + 7):nth-child(-n + 13) {
      @media only screen and (width >= 495px) {
        grid-column: span 6;
      }

      @media only screen and (width >= 768px) {
        grid-column: span 4;
      }
    }
  }

  @media only screen and (width >= 768px) {
    .top-ad-below-header.view-ads {
      padding: 24px 0;
      display: block;

      .adnm-topscroll & {
        padding: 0;
      }
    }
  }

  .top-ad-below-header {
    padding: var(--row-gap) 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
